import React, { useEffect, useState } from 'react';
import { ContentWrapper, SettingsSection, Wrapper } from './CargoTempConfig.style';
import { useVesselStore } from '../../../context/VesselContext';
import { CargoTemperatureConfig } from '../../../types/ViewModel/CargoTempLog/CargoTemperatureConfig';
import { TankDetails } from '../CargoTemperatureLogUtils';
import CargoInputFieldsComponent from './cargo-input-fields-component/CargoInputFieldsComponent';
import OdfjellInputComponent, { InputChangeData } from '../../common/odfjell-input-component/OdfjellInputComponent';
import { useCargoTempLogStore } from '../../../context/CargoTempLogContext';
import CargoTempConfigList from './CargoTempConfigList';
import { CargoTempLogOxygenUnitEnum } from '../../../enums/cargoTempLogOxygenUnitEnum';
import { IconButton } from '../../../styles/button.style';
import { DefaultWrapper } from 'src/components/ui/odfjellPanel/OdfjellPanel.styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ExpandSubHeading } from '../../../styles/heading.style';
import useReadOnlyUtils from 'src/utils/useReadOnlyUtils';

type Props = {
    cargoTanks: TankDetails[];
};

const LOG_SCHEDULES = {
    1: ['12:00'],
    2: ['08:00', '20:00'],
    3: ['00:00', '08:00', '16:00'],
    6: ['00:00', '04:00', '08:00', '12:00', '16:00', '20:00'],
};

const CargoTempConfigComponent: React.FC<Props> = (props) => {
    const { vessel } = useVesselStore();
    const {
        hasMissingConfigs,
        setHasMissingConfigs,
        cargoTemperatureConfigs,
        cargoTemperatureInputFields,
        setCargoTemperatureInputFields,
        filteredCargoTemperatureConfigs,
        setFilteredCargoTemperatureConfigs,
    } = useCargoTempLogStore();
    const [numberOfTimesToReport, setNumberOfTimesToReport] = useState<number>(1);
    const [oxygenUnitType, setOxygenUnitType] = useState<string>('%');
    const [showConfiguredCargoes, setShowConfiguredCargoes] = useState<boolean>(false);
    const [showAllConfigurations, setShowAllConfigurations] = useState<boolean>(false);
    const [showIndividualConfigurations, setShowIndividualConfigurations] = useState<boolean>(true);
    const { checkIsCargoTempLogReadOnly } = useReadOnlyUtils();

    useEffect(() => {
        if (!props.cargoTanks) return;
        updateConfig(props.cargoTanks);
    }, [props.cargoTanks, cargoTemperatureConfigs, vessel]);

    useEffect(() => {
        if (vessel && props?.cargoTanks && props.cargoTanks.length < 1) setHasMissingConfigs(false);
    }, [vessel, props.cargoTanks]);

    const isReadOnly = checkIsCargoTempLogReadOnly();

    const updateConfig = (cargoTanks: TankDetails[]) => {
        if (!cargoTemperatureConfigs || !vessel) return;
        const _inputFields = [...cargoTemperatureInputFields];

        const _missingCargoTemperatureConfigs: CargoTemperatureConfig[] = [];
        cargoTanks.forEach((tankDetails) => {
            let config = cargoTemperatureConfigs.find((c) => c.cargoNumber === tankDetails.cargoNumber);
            const alreadyAdded = _missingCargoTemperatureConfigs.find((c) => c.cargoNumber === tankDetails.cargoNumber);
            if (!config && !alreadyAdded) {
                config = {
                    imoNumber: vessel.imoNumber,
                    cargoNumber: tankDetails.cargoNumber,
                    logTimes: LOG_SCHEDULES[numberOfTimesToReport],
                    oxygenUnitType: oxygenUnitType,
                    logTimesPerDay: numberOfTimesToReport,
                    inputFields: _inputFields.filter((f) => f.isSelected),
                    tradeName: tankDetails.tradeName,
                    cargoTemperatureConfigId: 0,
                    inhibitorEffectivenessEntries: [],
                };
                _missingCargoTemperatureConfigs.push(config);
            }
        });

        setHasMissingConfigs(_missingCargoTemperatureConfigs.length > 0);

        // keep only configs for cargoes which are on board this date
        const _cargoTemperatureConfigs = [
            ...cargoTemperatureConfigs.filter((c) => cargoTanks.some((tankDetails) => tankDetails.cargoNumber === c.cargoNumber)),
        ].concat(_missingCargoTemperatureConfigs);
        setFilteredCargoTemperatureConfigs([..._cargoTemperatureConfigs]);
        setCargoTemperatureInputFields([..._inputFields]);
    };

    const onNumberOfTimesToReportChange = (changeObject: InputChangeData) => {
        const value = changeObject.event.target.value;
        setNumberOfTimesToReport(+value);
        updateLogTimes(+value);
    };

    const onOxygenUnitTypeChange = (changeObject: InputChangeData) => {
        const value = changeObject.event.target.value;
        setOxygenUnitType(value);
        updateOxygenUnitTypes(value);
    };

    const updateOxygenUnitTypes = (oxygenUnitType: string) => {
        if (!filteredCargoTemperatureConfigs || filteredCargoTemperatureConfigs.length === 0) return;
        filteredCargoTemperatureConfigs.forEach((config) => {
            if (config.cargoTemperatureConfigId === 0) {
                config.oxygenUnitType = oxygenUnitType;
            }
        });
        setFilteredCargoTemperatureConfigs([...filteredCargoTemperatureConfigs]);
    };

    const updateLogTimes = (numberOfTimesToReport: number) => {
        if (!filteredCargoTemperatureConfigs || filteredCargoTemperatureConfigs.length === 0) return;
        filteredCargoTemperatureConfigs.forEach((config) => {
            if (config.cargoTemperatureConfigId === 0) {
                config.logTimes = LOG_SCHEDULES[numberOfTimesToReport];
                config.logTimesPerDay = numberOfTimesToReport;
            }
        });
        setFilteredCargoTemperatureConfigs([...filteredCargoTemperatureConfigs]);
    };

    const toggleIsEditFieldsOpen = (cargoTemperatureConfig: CargoTemperatureConfig) => {
        const confIndex = filteredCargoTemperatureConfigs.findIndex((c) => c.cargoNumber === cargoTemperatureConfig.cargoNumber);
        const conf = filteredCargoTemperatureConfigs[confIndex];
        conf.isEditFieldsOpen = !conf.isEditFieldsOpen;
        filteredCargoTemperatureConfigs[confIndex] = conf;
        setFilteredCargoTemperatureConfigs([...filteredCargoTemperatureConfigs]);
    };

    const changeLogTimesPerDay = (changeObject: InputChangeData) => {
        const updatedConfigs = [...filteredCargoTemperatureConfigs];
        if (!Object.keys(LOG_SCHEDULES).includes(changeObject?.event?.target.value)) return;
        const confIndex = filteredCargoTemperatureConfigs.findIndex((c) => c.cargoNumber === changeObject?.dataObject?.config?.cargoNumber);
        if (confIndex < 0) return;

        updatedConfigs[confIndex] = {
            ...changeObject?.dataObject?.config,
            logTimesPerDay: changeObject.event.target.value,
            logTimes: LOG_SCHEDULES[changeObject.event.target.value],
        } as CargoTemperatureConfig;

        setFilteredCargoTemperatureConfigs(updatedConfigs);
    };

    const changeOxygenUnitTypeForChemical = (changeObject: InputChangeData) => {
        const updatedConfigs = [...filteredCargoTemperatureConfigs];
        if (!Object.values(CargoTempLogOxygenUnitEnum).includes(changeObject.event.target.value)) return;
        const confIndex = filteredCargoTemperatureConfigs.findIndex((c) => c.cargoNumber === changeObject?.dataObject?.config?.cargoNumber);
        if (confIndex < 0) return;

        updatedConfigs[confIndex] = {
            ...changeObject?.dataObject?.config,
            oxygenUnitType: changeObject.event.target.value,
        } as CargoTemperatureConfig;

        setFilteredCargoTemperatureConfigs(updatedConfigs);
    };

    const toggleShowConfiguredCargoes = () => {
        if (showConfiguredCargoes) {
            setShowConfiguredCargoes(false);
        } else {
            setShowConfiguredCargoes(true);
        }
    };

    const toggleShowAllConfigurations = () => {
        if (showAllConfigurations) {
            setShowAllConfigurations(false);
            setShowIndividualConfigurations(true);
        } else {
            setShowAllConfigurations(true);
            setShowIndividualConfigurations(false);
        }
    };

    const toggleShowIndividualConfigurations = () => {
        if (showIndividualConfigurations) {
            setShowIndividualConfigurations(false);
            setShowAllConfigurations(true);
        } else {
            setShowIndividualConfigurations(true);
            setShowAllConfigurations(false);
        }
    };

    return (
        <Wrapper>
            <ContentWrapper>
                {hasMissingConfigs && (
                    <>
                        <ExpandSubHeading>
                            Configure new cargoes individually{' '}
                            <IconButton
                                className="grey-button"
                                isSelected={showIndividualConfigurations}
                                onClick={() => toggleShowIndividualConfigurations()}
                            >
                                <FontAwesomeIcon title="Click to change sorting" icon={'chevron-down'} />
                            </IconButton>
                        </ExpandSubHeading>
                        {showIndividualConfigurations && (
                            <CargoTempConfigList
                                newCargo={true}
                                numberOfTimesToReport={numberOfTimesToReport}
                                commonOxygenUnitType={oxygenUnitType}
                                filteredCargoTemperatureConfigs={filteredCargoTemperatureConfigs}
                                cargoTanks={props.cargoTanks}
                                setFilteredCargoTemperatureConfigs={setFilteredCargoTemperatureConfigs}
                                toggleIsEditFieldsOpen={toggleIsEditFieldsOpen}
                                changeLogTimesPerDay={changeLogTimesPerDay}
                                changeOxygenUnitTypeForChemical={changeOxygenUnitTypeForChemical}
                            />
                        )}
                    </>
                )}
                {hasMissingConfigs && (
                    <>
                        <ExpandSubHeading>
                            Set one configuration for all new cargoes{' '}
                            <IconButton className="grey-button" isSelected={showAllConfigurations} onClick={() => toggleShowAllConfigurations()}>
                                <FontAwesomeIcon title="Click to change sorting" icon={'chevron-down'} />
                            </IconButton>
                        </ExpandSubHeading>
                        {showAllConfigurations && (
                            <SettingsSection>
                                <h4>All new cargo</h4>
                                <OdfjellInputComponent
                                    isReadOnly={isReadOnly}
                                    type={'select'}
                                    options={Object.keys(LOG_SCHEDULES)}
                                    onChange={onNumberOfTimesToReportChange}
                                    value={numberOfTimesToReport}
                                    name={'numberOfTimesToReport'}
                                    disableUnit={true}
                                    description={'  Number of times to report per day:'}
                                ></OdfjellInputComponent>

                                <DefaultWrapper>
                                    <CargoInputFieldsComponent
                                        keyName={`all_new_fields`}
                                        filteredCargoTemperatureConfigs={filteredCargoTemperatureConfigs}
                                        setFilteredCargoTemperatureConfigs={setFilteredCargoTemperatureConfigs}
                                    ></CargoInputFieldsComponent>
                                    <OdfjellInputComponent
                                        isReadOnly={isReadOnly}
                                        type={'select'}
                                        options={Object.values(CargoTempLogOxygenUnitEnum)}
                                        onChange={onOxygenUnitTypeChange}
                                        value={oxygenUnitType}
                                        name={'oxygenUnitType'}
                                        disableUnit={true}
                                        description={'    Oxygen unit type: '}
                                    ></OdfjellInputComponent>
                                </DefaultWrapper>
                            </SettingsSection>
                        )}
                    </>
                )}

                {hasMissingConfigs && (
                    <ExpandSubHeading>
                        Show previously configured cargoes{' '}
                        <IconButton className="grey-button" isSelected={showConfiguredCargoes} onClick={() => toggleShowConfiguredCargoes()}>
                            <FontAwesomeIcon title="Click to change sorting" icon={'chevron-down'} />
                        </IconButton>
                    </ExpandSubHeading>
                )}
                {(!hasMissingConfigs || showConfiguredCargoes) && (
                    <CargoTempConfigList
                        newCargo={false}
                        numberOfTimesToReport={numberOfTimesToReport}
                        commonOxygenUnitType={oxygenUnitType}
                        filteredCargoTemperatureConfigs={filteredCargoTemperatureConfigs}
                        cargoTanks={props.cargoTanks}
                        setFilteredCargoTemperatureConfigs={setFilteredCargoTemperatureConfigs}
                        toggleIsEditFieldsOpen={toggleIsEditFieldsOpen}
                        changeLogTimesPerDay={changeLogTimesPerDay}
                        changeOxygenUnitTypeForChemical={changeOxygenUnitTypeForChemical}
                    />
                )}
            </ContentWrapper>
        </Wrapper>
    );
};

export default CargoTempConfigComponent;
